import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Users, Clock, Award } from "lucide-react";
import useScrollReveal from "../hooks/useScrollReveal";
import useEmblaCarousel from "embla-carousel-react";

const FEATURES = [
  "Premium quality ingredients sourced from trusted farmers",
  "Hygienic packaging with strict quality control standards",
  "Wide range of authentic Indian flavors in every pack",
  "Consistent taste and freshness across all products",
  "Pan-India distribution reaching every corner of the country",
];

const STATS = [
  { value: "40+", label: "Years Experience", icon: Clock },
  { value: "1M+", label: "Happy Customers", icon: Users },
  { value: "50+", label: "Products", icon: Award },
];

const ABOUT_IMAGES = [
  "/redchips.png",
  "/Chatpata-Bnbn_zpU.png",
  "/Aloo-oUrpi2pO.png",
  "/Salted-lkWK5iNw.png",
];

const About = () => {
  const [leftRef, leftVisible] = useScrollReveal(0.1);
  const [rightRef, rightVisible] = useScrollReveal(0.1);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  useEffect(() => {
    if (emblaApi) {
      const interval = setInterval(() => {
        emblaApi.scrollNext();
      }, 2500);
      return () => clearInterval(interval);
    }
  }, [emblaApi]);

  return (
    <section
      id="about"
      className="py-24 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Image */}
          <div
            ref={leftRef}
            className={`relative transition-all duration-700 ${
              leftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"
            }`}
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-yellow-50 to-orange-100">
              <div className="overflow-hidden" ref={emblaRef}>
                <div className="flex h-72 sm:h-96 lg:h-[480px] items-center">
                  {ABOUT_IMAGES.map((img, idx) => (
                    <div key={idx} className="flex-[0_0_100%] min-w-0 flex flex-col items-center justify-center p-6 sm:p-10">
                      <img
                        src={img}
                        alt={`MunchhoNN Snack ${idx+1}`}
                        className="w-full h-full object-contain mix-blend-multiply drop-shadow-2xl"
                      />
                    </div>
                  ))}
                </div>
              </div>
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-orange-900/20 via-transparent to-transparent" />
            </div>

            {/* Top accent */}
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-gradient-to-br from-orange-400 to-yellow-400 rounded-2xl opacity-20 blur-sm" />
          </div>

          {/* Right: Content */}
          <div
            ref={rightRef}
            className={`space-y-8 transition-all duration-700 delay-150 ${
              rightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"
            }`}
          >
            <div>
              <span className="text-sm font-semibold text-orange-500 uppercase tracking-widest">
                Our Story
              </span>
              <h2 className="text-4xl sm:text-5xl font-bold text-stone-900 mt-2 leading-tight">
                Crafted With{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
                  Passion
                </span>
                ,<br />
                Delivered With{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-orange-500">
                  Love
                </span>
              </h2>
              <p className="text-stone-600 mt-4 text-lg leading-relaxed">
                For over four decades, MunchhoNN has been crafting snacks that
                bring families together. From the heart of New Delhi to every
                corner of India, we deliver joy in every pack.
              </p>
            </div>

            {/* Feature list */}
            <ul className="space-y-3">
              {FEATURES.map((feat, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <CheckCircle2
                    size={20}
                    className="text-orange-500 mt-0.5 flex-shrink-0"
                  />
                  <span className="text-stone-600 text-base">{feat}</span>
                </li>
              ))}
            </ul>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              {STATS.map(({ value, label, icon: Icon }) => (
                <div
                  key={label}
                  className="bg-white rounded-2xl p-4 text-center shadow-sm border border-orange-100 hover:shadow-md hover:-translate-y-1 transition-all duration-200"
                  data-testid={`about-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <Icon size={20} className="text-orange-400 mx-auto mb-2" />
                  <p className="text-2xl font-black text-orange-500">{value}</p>
                  <p className="text-xs text-stone-500 font-semibold leading-tight mt-1">
                    {label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
